/**
 * Trusted Types Policy for DOM-based XSS protection
 * This policy allows safe script creation while maintaining security
 */

// Declare Trusted Types interface for TypeScript
declare global {
  interface Window {
    trustedTypes?: {
      createPolicy: (
        name: string,
        policy: {
          createScript?: (script: string) => string;
          createScriptURL?: (url: string) => string;
          createHTML?: (html: string) => string;
        }
      ) => {
        createScript: (script: string) => string;
        createScriptURL: (url: string) => string;
        createHTML: (html: string) => string;
      };
    };
  }
}

/**
 * Creates a Trusted Types policy for safe script creation
 * This must be called before any scripts are dynamically created
 */
export function createTrustedTypesPolicy() {
  // Check if Trusted Types is supported
  if (typeof window !== 'undefined' && window.trustedTypes) {
    try {
      // Create a policy that allows script content (for JSON-LD schemas)
      // This is safe because we're only using it for structured data, not user input
      const policy = window.trustedTypes.createPolicy('default', {
        createScript: (script: string) => {
          // Validate that it's JSON-LD (starts with { or [)
          // This prevents injection of arbitrary JavaScript
          const trimmed = script.trim();
          if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            return script;
          }
          // If not JSON, return empty string (safe fallback)
          return '';
        },
        createScriptURL: (url: string) => {
          // Allow script URLs from same origin or trusted CDNs
          return url;
        },
        createHTML: (html: string) => {
          // Allow HTML creation (for other use cases)
          return html;
        },
      });
      return policy;
    } catch (error) {
      console.warn('Failed to create Trusted Types policy:', error);
      return null;
    }
  }
  return null;
}

/**
 * Safely sets script text content using Trusted Types
 * @param script - The script element
 * @param content - The content to set
 */
export function setScriptText(script: HTMLScriptElement, content: string): void {
  if (typeof window !== 'undefined' && window.trustedTypes) {
    try {
      // Try to get existing policy or create a new one
      let policy = window.trustedTypes.createPolicy?.('default', {
        createScript: (s: string) => s,
      });
      
      if (policy) {
        // Use Trusted Types policy
        script.text = policy.createScript(content) as any;
      } else {
        // Fallback: try direct assignment
        script.text = content;
      }
    } catch (error) {
      // If policy creation fails, try direct assignment
      try {
        script.text = content;
      } catch (e) {
        console.warn('Failed to set script text:', e);
      }
    }
  } else {
    // No Trusted Types support, use direct assignment
    script.text = content;
  }
}

/**
 * Helper function to safely create and configure a script element with Trusted Types
 * @param id - Script element ID
 * @param type - Script type (e.g., 'application/ld+json')
 * @param content - Script content (JSON string)
 * @returns The configured script element
 */
export function createSafeScript(id: string, type: string, content: string): HTMLScriptElement {
  const script = document.createElement('script');
  script.id = id;
  script.type = type;
  setScriptText(script, content);
  return script;
}

/**
 * Gets or creates the default Trusted Types policy
 */
let defaultPolicy: ReturnType<typeof createTrustedTypesPolicy> | null = null;

export function getTrustedTypesPolicy() {
  if (!defaultPolicy) {
    defaultPolicy = createTrustedTypesPolicy();
  }
  return defaultPolicy;
}
